import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Activity, Award, Trophy, Star, Shield, Target, TrendingUp, Filter } from 'lucide-react';
import { PlayerOfWeekPanel } from '@/components/stats/potw/PlayerOfWeekPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AWARD_ICONS = {
  mvp: Trophy,
  scoring: Target,
  rebounding: Activity,
  assists: TrendingUp,
  dpoy: Shield,
  mip: Star,
};

const AWARD_COLORS = {
  mvp: 'from-amber-400 to-amber-600',
  scoring: 'from-red-400 to-red-600',
  rebounding: 'from-blue-400 to-blue-600',
  assists: 'from-purple-400 to-purple-600',
  dpoy: 'from-green-400 to-green-600',
  mip: 'from-cyan-400 to-cyan-600',
};

function AwardCard({ award }) {
  const Icon = AWARD_ICONS[award.key] || Trophy;
  const gradient = AWARD_COLORS[award.key] || 'from-slate-400 to-slate-600';
  const w = award.winner;

  return (
    <Card className="overflow-hidden hover:-translate-y-1 transition-transform duration-300" data-testid={`award-${award.key}`}>
      <div className={`bg-gradient-to-br ${gradient} p-4 text-white`}>
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-5 h-5" />
          <h3 className="text-sm font-bold uppercase tracking-wide">{award.title}</h3>
        </div>
        <p className="text-xs text-white/70">{award.description}</p>
      </div>
      <CardContent className="p-4">
        {/* Winner */}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-lg shrink-0">
            #{w.jersey}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-800 truncate">{w.name}</div>
            <div className="text-xs text-slate-500">{w.team_name} &middot; {w.gp} GP</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black text-[#00247D]">{w.stat_value}</div>
          </div>
        </div>

        {/* Runners-up */}
        {award.runners_up.length > 0 && (
          <div className="border-t border-slate-100 pt-2 space-y-1.5">
            {award.runners_up.map((r, i) => (
              <div key={r.player_id} className="flex items-center gap-2 text-sm">
                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold">{i + 2}</span>
                <span className="font-mono text-xs text-slate-400">#{r.jersey}</span>
                <span className="flex-1 text-slate-600 truncate">{r.name}</span>
                <span className="text-xs text-slate-500">{r.team_name}</span>
                <span className="font-semibold text-slate-700">{r.stat_value}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PerTable({ players }) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="per-table">
            <thead>
              <tr className="bg-[#00247D] text-white text-[10px] font-bold uppercase">
                <th className="px-3 py-2 text-center w-8">RK</th>
                <th className="px-3 py-2 w-8">#</th>
                <th className="px-3 py-2">Player</th>
                <th className="px-3 py-2">Team</th>
                <th className="px-3 py-2 text-center">GP</th>
                <th className="px-3 py-2 text-center bg-amber-500/20">PER</th>
                <th className="px-3 py-2 text-center">PPG</th>
                <th className="px-3 py-2 text-center">RPG</th>
                <th className="px-3 py-2 text-center">APG</th>
                <th className="px-3 py-2 text-center">SPG</th>
                <th className="px-3 py-2 text-center">BPG</th>
                <th className="px-3 py-2 text-center">TOPG</th>
                <th className="px-3 py-2 text-center">FG%</th>
                <th className="px-3 py-2 text-center">TS%</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p, i) => (
                <tr key={p.player_id} className={`border-b border-slate-100 ${i < 3 ? 'bg-amber-50/30' : i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                  <td className="px-3 py-2 text-center text-xs font-bold text-slate-400">{i + 1}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{p.jersey}</td>
                  <td className="px-3 py-2 text-sm font-semibold text-[#00247D] hover:underline cursor-pointer" onClick={() => window.location.href = `/stats/player/${p.player_id}`}>{p.name}</td>
                  <td className="px-3 py-2 text-sm text-slate-500">{p.team_name}</td>
                  <td className="px-3 py-2 text-sm text-center">{p.gp}</td>
                  <td className="px-3 py-2 text-sm text-center font-black text-[#00247D] bg-amber-50/50">{p.per}</td>
                  <td className="px-3 py-2 text-sm text-center">{p.ppg}</td>
                  <td className="px-3 py-2 text-sm text-center">{p.rpg}</td>
                  <td className="px-3 py-2 text-sm text-center">{p.apg}</td>
                  <td className="px-3 py-2 text-sm text-center">{p.spg}</td>
                  <td className="px-3 py-2 text-sm text-center">{p.bpg}</td>
                  <td className="px-3 py-2 text-sm text-center text-red-500">{p.topg}</td>
                  <td className="px-3 py-2 text-sm text-center">{p.fg_pct}%</td>
                  <td className="px-3 py-2 text-sm text-center">{p.ts_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PerAndAwards() {
  const navigate = useNavigate();
  const { isAuthenticated, isPro, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('awards');
  const [awards, setAwards] = useState([]);
  const [perData, setPerData] = useState(null);
  const [loading, setLoading] = useState(true);
  // "all" = no tournament filter. Otherwise stores the event_id we filter on.
  // Both tabs share the filter so switching tabs preserves the user's choice.
  const [searchParams] = useSearchParams();
  const [eventFilter, setEventFilter] = useState(searchParams.get('event_id') || 'all');
  // De-duped union of available_events from BOTH endpoints. Rendered in the
  // Select so the filter shows every tournament with tracked stats — even if
  // one endpoint has a game the other doesn't (unlikely but defensive).
  const [availableEvents, setAvailableEvents] = useState([]);
  const token = localStorage.getItem('session_token');

  // Wrap in useCallback so useEffect's dep array can safely reference them
  // without re-fetching on every render.
  const fetchAwards = useCallback(async (eid) => {
    try {
      const url = `${API_URL}/api/stats/season-awards${eid && eid !== 'all' ? `?event_id=${encodeURIComponent(eid)}` : ''}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setAwards(d.awards || []);
        return d.available_events || [];
      }
    } catch (e) { console.error(e); }
    return [];
  }, [token]);

  const fetchPer = useCallback(async (eid) => {
    try {
      const url = `${API_URL}/api/stats/per-rankings${eid && eid !== 'all' ? `?event_id=${encodeURIComponent(eid)}` : ''}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setPerData(d);
        return d.available_events || [];
      }
    } catch (e) { console.error(e); }
    return [];
  }, [token]);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated || !isPro) { navigate('/stats'); return; }
    Promise.all([fetchAwards(eventFilter), fetchPer(eventFilter)]).then(([a, b]) => {
      // Merge + dedupe availableEvents from both endpoints. Only refresh from
      // the unfiltered fetch (eventFilter === 'all') to avoid the list
      // collapsing to a single option when a filter is active.
      if (eventFilter === 'all' || !availableEvents.length) {
        const byId = {};
        [...a, ...b].forEach(e => { byId[e.id] = e; });
        setAvailableEvents(Object.values(byId).sort((x, y) => (x.name || '').localeCompare(y.name || '')));
      }
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isPro, isLoading, eventFilter]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto pl-3 pr-[92px] sm:pl-6 sm:pr-[170px] 2xl:pr-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/stats')} className="px-2 sm:px-3 shrink-0"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-base sm:text-xl font-bold text-[#00247D] truncate" style={{ fontFamily: 'Barlow Condensed' }}>
              <span className="hidden sm:inline">AWARDS & EFFICIENCY</span>
              <span className="sm:hidden">AWARDS</span>
            </h1>
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 shrink-0">
            <button className={`px-2 sm:px-3 py-1 text-xs font-bold rounded flex items-center gap-1 ${activeTab === 'awards' ? 'bg-white shadow text-[#00247D]' : 'text-slate-500'}`}
              onClick={() => setActiveTab('awards')} data-testid="tab-awards"
              aria-label="Awards">
              <Award className="w-3.5 h-3.5" /><span className="hidden sm:inline">Awards</span>
            </button>
            <button className={`px-2 sm:px-3 py-1 text-xs font-bold rounded flex items-center gap-1 ${activeTab === 'per' ? 'bg-white shadow text-[#00247D]' : 'text-slate-500'}`}
              onClick={() => setActiveTab('per')} data-testid="tab-per"
              aria-label="PER Rankings">
              <Activity className="w-3.5 h-3.5" /><span className="hidden sm:inline">PER Rankings</span>
            </button>
            <button className={`px-2 sm:px-3 py-1 text-xs font-bold rounded flex items-center gap-1 ${activeTab === 'potw' ? 'bg-white shadow text-[#00247D]' : 'text-slate-500'}`}
              onClick={() => setActiveTab('potw')} data-testid="tab-potw"
              aria-label="Player of the Week">
              <Star className="w-3.5 h-3.5" /><span className="hidden sm:inline">Player of the Week</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6">
        {/* Tournament filter — shared across both tabs so the user's scope
            choice persists when they flip between Awards and PER Rankings. */}
        <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="tournament-filter-row">
          <div className="inline-flex items-center gap-2 text-slate-500 text-sm">
            <Filter className="w-4 h-4" />
            <span className="font-medium">Tournament:</span>
          </div>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-full sm:w-[280px] h-9" data-testid="tournament-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="tournament-filter-all">All Tournaments</SelectItem>
              {eventFilter !== 'all' && !availableEvents.some(ev => ev.id === eventFilter) && (
                <SelectItem value={eventFilter} data-testid="tournament-filter-current">This league (no stats yet)</SelectItem>
              )}
              {availableEvents.map(ev => (
                <SelectItem key={ev.id} value={ev.id} data-testid={`tournament-filter-${ev.id}`}>
                  {ev.name} {ev.games_count ? `(${ev.games_count} game${ev.games_count !== 1 ? 's' : ''})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {availableEvents.length === 0 && (
            <span className="text-xs text-slate-400">
              Link games to a league match at setup to enable filtering.
            </span>
          )}
        </div>

        {activeTab === 'awards' && (
          <>
            {awards.length === 0 ? (
              <div className="text-center py-16">
                <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {eventFilter === 'all'
                    ? 'Complete more games to see season awards.'
                    : 'No completed games in this tournament yet.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="awards-grid">
                {awards.map(award => <AwardCard key={award.key} award={award} />)}
              </div>
            )}
          </>
        )}

        {activeTab === 'potw' && <PlayerOfWeekPanel key={eventFilter} eventId={eventFilter} />}

        {activeTab === 'per' && (
          <>
            {!perData || perData.players.length === 0 ? (
              <div className="text-center py-16">
                <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {eventFilter === 'all'
                    ? 'Complete games to see PER rankings.'
                    : 'No completed games in this tournament yet.'}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm text-slate-500">{perData.games_count} game{perData.games_count !== 1 ? 's' : ''} analyzed</p>
                  <div className="text-xs text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                    PER = (PTS + REB + AST + STL + BLK - MissedFG - MissedFT - TO) / GP
                  </div>
                </div>
                <PerTable players={perData.players} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
